import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", user_id: "", contact: "", password: "" });

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => {
        setInfo(r.data);
        // Suggest a user_id from the email local part
        const local = (r.data.email || "").split("@")[0];
        setForm((f) => ({ ...f, user_id: local }));
      })
      .catch((e) => setError(e.response?.data?.detail || "Invite not found or expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/invites/accept", { token, ...form });
      toast.success(`Welcome to ${info.business_name}!`);
      if (refresh) await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative pb-12" data-testid="accept-invite-page">
      <div className="flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm fade-up">
          <div className="overline mb-3">You're invited</div>
          {loading && <div className="text-sm text-[#6B7280]">Verifying invite…</div>}
          {!loading && error && (
            <div data-testid="invite-error">
              <h1 className="font-display text-3xl font-bold tracking-tighter text-[#EF4444] mb-2">Invite invalid</h1>
              <p className="text-sm text-[#6B7280]">{error}</p>
              <Button onClick={() => navigate("/login")} className="mt-6 rounded-none bg-[#0F172A] text-white">Go to login</Button>
            </div>
          )}
          {!loading && info && (
            <>
              <h1 className="font-display text-3xl font-bold tracking-tighter text-[#0F172A] mb-2">Join {info.business_name}</h1>
              <p className="text-sm text-[#6B7280] mb-2">
                <strong>{info.invited_by_name}</strong> invited <span className="font-mono">{info.email}</span> to join as <strong>{info.role}</strong>.
              </p>
              <p className="text-xs text-[#6B7280] mb-8">Set your credentials below to complete signup.</p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
                <div>
                  <Label className="overline mb-1.5 block">Your name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                    data-testid="accept-name" placeholder="Full name" className="rounded-none h-10" />
                </div>
                <div>
                  <Label className="overline mb-1.5 block">Pick a User ID</Label>
                  <Input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required minLength={2}
                    data-testid="accept-user-id" placeholder="e.g. ali" className="rounded-none h-10 font-mono" />
                </div>
                <div>
                  <Label className="overline mb-1.5 block">Contact (optional)</Label>
                  <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    data-testid="accept-contact" placeholder="+92-300-1234567" className="rounded-none h-10" />
                </div>
                <div>
                  <Label className="overline mb-1.5 block">Password</Label>
                  <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required
                    testid="accept-password" className="rounded-none h-10" />
                  <p className="text-[10px] text-[#6B7280] mt-1">Minimum 6 characters.</p>
                </div>
                <Button type="submit" disabled={busy} data-testid="accept-submit"
                  className="w-full h-10 rounded-none bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold uppercase tracking-wider">
                  {busy ? "Joining…" : "Accept & continue"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="relative hidden lg:block bg-[#0F172A]">
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="overline text-white/70 mb-3">Swift Register</div>
          <h2 className="font-display text-5xl font-bold leading-tight tracking-tighter max-w-md">
            Welcome to your team's books.
          </h2>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-white">
        <Footer />
      </div>
    </div>
  );
}
